"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import PrintNoteDialog from "@/components/scribe/print-note-dialog";
import { useUser } from "@/contexts/user-provider";
import { UseNotePrintFlowResult } from "@/hooks/use-note-print-flow";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { NoteDetail } from "@/types/notes";
import { useFaxPhoneNumbers } from "@/api/provider/fax/phone-numbers-query";

// Loaded on demand: the composer drags in @react-pdf/renderer (for the cover
// sheet preview), and every note view mounts this host. Only a provider who
// actually faxes a note should pay for that bundle.
const ComposeFaxDialog = dynamic(() => import("@/components/fax/compose-fax-dialog").then(m => m.ComposeFaxDialog), {
  ssr: false
});
interface NotePrintDialogHostProps {
  printFlow: UseNotePrintFlowResult;
}

/**
 * Split out so the fax-line lookup only runs while the dialog is actually open
 * — every note view mounts the host, and none of them should pay for a query
 * they may never need.
 */
function PrintNoteDialogWithFax(t0) {
  const $ = _c(14);
  const {
    printFlow,
    noteDetail
  } = t0;
  const {
    state,
    actions
  } = printFlow;
  const {
    currentUser
  } = useUser();
  const faxPhoneNumbersQuery = useFaxPhoneNumbers();
  let t1;
  if ($[0] !== currentUser || $[1] !== faxPhoneNumbersQuery) {
    t1 = hasPermission(currentUser, PERMISSIONS.MESSAGING_FAX_WRITE) && (faxPhoneNumbersQuery.data?.length ?? 0) > 0;
    $[0] = currentUser;
    $[1] = faxPhoneNumbersQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const canFax = t1;
  let t2;
  if ($[3] !== actions) {
    t2 = open => {
      if (!open) {
        actions.closeDialog();
      }
    };
    $[3] = actions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== actions.confirmPrint || $[6] !== canFax || $[7] !== noteDetail || $[8] !== state.isDialogOpen || $[9] !== state.isFaxing || $[10] !== state.isPrinting || $[11] !== state.photoCount || $[12] !== t2) {
    t3 = <PrintNoteDialog open={state.isDialogOpen} onOpenChange={t2} noteDetail={noteDetail} photoCount={state.photoCount} onConfirm={actions.confirmPrint} isPrinting={state.isPrinting} isFaxing={state.isFaxing} canFax={canFax} />;
    $[5] = actions.confirmPrint;
    $[6] = canFax;
    $[7] = noteDetail;
    $[8] = state.isDialogOpen;
    $[9] = state.isFaxing;
    $[10] = state.isPrinting;
    $[11] = state.photoCount;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
export function NotePrintDialogHost(t0) {
  const $ = _c(9);
  const {
    printFlow
  } = t0;
  const {
    state,
    actions
  } = printFlow;
  let t1;
  if ($[0] !== printFlow || $[1] !== state.noteDetailForDialog) {
    t1 = state.noteDetailForDialog && <PrintNoteDialogWithFax printFlow={printFlow} noteDetail={state.noteDetailForDialog} />;
    $[0] = printFlow;
    $[1] = state.noteDetailForDialog;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== actions || $[4] !== state.faxHandoff) {
    t2 = state.faxHandoff && <ComposeFaxDialog open={true} onOpenChange={open => {
      if (!open) {
        actions.closeFaxDialog();
      }
    }} patientId={state.faxHandoff.patientId} initialAttachments={[state.faxHandoff.attachment]} onSent={actions.closeFaxDialog} />;
    $[3] = actions;
    $[4] = state.faxHandoff;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}